// Сессии для слоёв: распаковка ответа /getSessions и восстановление показанных
// заголовков для старых сессий по балансировщику и его снимкам.
import axios from 'axios';
import { apiHost } from './statFilters';
import { normalizePhrase, buildIndex, getBalansirIndex } from './balansirIndex';
import { dayOf } from './layers';

const DAY = 864e5;
// Снимки старше 90 дней бэкенд чистит, глубже восстанавливать не из чего.
const SHOWN_DEPTH = 90 * DAY;

const stripTags = s => String(s).replace(/<[^>]*>/g, '');

// Правило балансировщика (К3) → {role, selector, action, value}. Порядок проверок как
// у extractReplaceRules: hidden важнее текста, непустой innerHTML — это HTML.
export function normRule(item) {
  const r = item || {};
  const base = { role: r.role || '', selector: r.elementSelectorAll || '' };
  if (r.hidden) return { ...base, action: 'hidden', value: '' };
  if (r.innerHTML) return { ...base, action: 'html', value: stripTags(r.innerHTML) };
  if (r.href) return { ...base, action: 'href', value: String(r.href) };
  return { ...base, action: 'text', value: r.text == null ? '' : String(r.text) };
}

function parseRules(json) {
  if (!json) return null;
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr.map(normRule) : null;
  } catch (e) {
    return null;
  }
}

export function unpackSessions(resp) {
  const r = resp || {};
  const sessions = (r.sessions || []).map(row => withHeads({
    id: row[0], start: row[1], day: dayOf(row[1]),
    phrase: row[2], dev: row[3],
    churn: row[4], reached: row[5],
    convMask: row[6], convFirst: row[7],
    variant: row[8] || null,
    title: row[9] == null ? null : stripTags(row[9]),
    subtitle: row[10] == null ? null : stripTags(row[10]),
    rules: parseRules(row[11]),
    shownSource: row[8] ? 'log' : null,
  }));
  return { blocks: r.blocks || [], convTypes: r.convTypes || [], sessions };
}

// Бэк пишет shownTitle/shownSubtitle только по ролям (dbAdd.js startSession). Пока роли в
// балансировщике не проставлены, заголовок берём из записанных правил той же сессии.
function withHeads(s) {
  if (s.variant && s.title == null && s.subtitle == null && s.rules && s.rules.length) {
    const [title, subtitle] = pickHeads(s.rules);
    s.title = title; s.subtitle = subtitle;
  }
  return s;
}

// Map, а не {}: фраза приходит от посетителя и может быть «__proto__».
export function indexByPhrase(sessions) {
  const map = new Map();
  for (const s of sessions) {
    if (!map.has(s.phrase)) map.set(s.phrase, []);
    map.get(s.phrase).push(s);
  }
  return map;
}

export function sessionsOfPhrases(byPhrase, phrases) {
  const out = [];
  // без спреда: у одной фразы бывает больше сессий, чем лимит аргументов вызова
  for (const p of phrases) for (const s of byPhrase.get(p) || []) out.push(s);
  return out.sort((a, b) => a.start - b.start || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

// Вариант = чей трекер грузит сниппет: у A и B разные токены. Если адреса совпадают
// или токен не найден ни в одном — подмены для этой страницы не было (совпавшие адреса — см. ниже).
function variantOf(rec, token) {
  const file = 'tracker_' + token + '.js';
  const a = String(rec.urlA || '').includes(file);
  const b = String(rec.urlB || '').includes(file);
  // Один трекер на обе ветки: какая показана, по токену не понять, считаем A.
  return a ? 'A' : b ? 'B' : null;
}

// Роли появились только с v14. Если хоть у одного правила роль есть, правила уже
// размечены редактором, и выбор только по ролям: правило «прочее» (роль '') или
// «подзаголовок» не должно позиционно стать заголовком. В старых правилах ролей нет —
// тогда по шаблону «первое правило заголовок, второе подзаголовок»
// (templates/testAB Подмена текстов.js), и считаются только text/html: у href значение —
// адрес ссылки, у hidden значения нет, заголовком они не бывают.
// Пустой текст — null: «заголовка нет», одинаково для headStats и ruleChangesFrom.
export function pickHeads(rules) {
  const val = r => (r && r.value) || null;
  if (rules.some(r => r.role)) return [val(rules.find(r => r.role === 'title')), val(rules.find(r => r.role === 'subtitle'))];
  // Без букв — телефон или номер (у заказчика первым правилом во всех записях стоит телефон),
  // заголовком такое не бывает.
  const texts = rules.filter(r => (r.action === 'text' || r.action === 'html') && /[a-zа-яё]/i.test(r.value));
  return [val(texts[0]), val(texts[1])];
}

// Состояние балансировщика на момент T = data первого снимка с createdAt > T
// (снимок пишется ДО изменения), иначе текущие записи.
export function resolveShown(sessions, token, balansirRecords, snapshots, now) {
  const snaps = (snapshots || []).slice().sort((a, b) => a.createdAt - b.createdAt)
    .map(s => ({ at: s.createdAt, data: s.data, idx: null }));
  let current = null;
  const indexAt = start => {
    const s = snaps.find(x => x.at > start);
    if (s) return s.idx || (s.idx = buildIndex(s.data));
    return current || (current = buildIndex(balansirRecords));
  };
  const from = now - SHOWN_DEPTH;
  for (const s of sessions) {
    if (s.shownSource !== null) continue;
    const shown = s.start >= from ? shownFor(indexAt(s.start), s.phrase, token) : null;
    if (shown) { Object.assign(s, shown, { shownSource: 'snapshot' }); continue; }
    // Показ не записан и снимка нет (старше 90 дней): по просьбе заказчика считаем, что
    // показывалось то, что стоит на подмену сейчас. Источник помечен, чтобы не путать с записанным.
    const now2 = shownFor(current || (current = buildIndex(balansirRecords)), s.phrase, token);
    if (now2) Object.assign(s, now2, { shownSource: 'current' });
  }
  return sessions;
}

// Что стоит на подмене по фразе для страницы этого токена сейчас (index — getBalansirIndex).
export function shownNow(index, phrase, token) { return index ? shownFor(index, phrase, token) : null; }

// Что показывалось по фразе странице этого токена в данном состоянии балансировщика.
function shownFor(index, phrase, token) {
  for (const r of index.get(normalizePhrase(phrase)) || []) {
    const variant = variantOf(r, token);
    if (!variant) continue;
    const rules = ((variant === 'A' ? r.blockReWriteA : r.blockReWriteB) || []).map(normRule);
    const [title, subtitle] = pickHeads(rules);
    return { variant, rules, title, subtitle };
  }
  return null;
}

// Даты смен заголовка/подзаголовка у фраз столбца — для события «смена правил».
// Состояния по времени: снимки по возрастанию createdAt, затем текущие записи. Снимок
// пишется ДО изменения, поэтому разница между снимком k и следующим состоянием
// случилась в момент снимка k.
export function ruleChangesFrom(snapshots, currentRecords, phrases, token) {
  const snaps = (snapshots || []).slice().sort((a, b) => a.createdAt - b.createdAt);
  const states = snaps.map(s => buildIndex(s.data)).concat([buildIndex(currentRecords)]);
  const q = v => (v == null ? '—' : '«' + v + '»');
  const out = [];
  snaps.forEach((snap, k) => {
    const parts = [];
    for (const ph of phrases) {
      const was = shownFor(states[k], ph, token) || {};
      const now = shownFor(states[k + 1], ph, token) || {};
      if ((was.title || null) !== (now.title || null))
        parts.push(`заголовок: ${q(was.title)} → ${q(now.title)} · ${ph}`);
      if ((was.subtitle || null) !== (now.subtitle || null))
        parts.push(`подзаголовок: ${q(was.subtitle)} → ${q(now.subtitle)} · ${ph}`);
    }
    if (parts.length) out.push({ at: snap.createdAt, note: parts.join('; ') });
  });
  return out;
}

const cache = new Map();

export function loadSessions(params) {
  const key = JSON.stringify(params);
  if (!cache.has(key)) {
    const p = axios.post(apiHost() + '/api/v1/getSessions', params)
      .then(r => unpackSessions(r.data))
      .catch(e => { cache.delete(key); throw e; });
    cache.set(key, p);
  }
  return cache.get(key);
}

export function clearSessionsCache() { cache.clear(); }

// Neo4j toString(datetime) бывает с зоной в скобках: «…+03:00[Europe/Moscow]».
const parseTs = s => Date.parse(String(s).replace(/\[.*\]$/, ''));

let shownPromise = null;

// Источники для resolveShown: текущие записи и снимки за 90 дней. Один раз на жизнь SPA,
// как индекс балансировщика.
// ponytail: все снимки окна тянутся параллельно по одному; если их станут сотни — нужен пакетный эндпоинт.
export function loadShownSources(now = Date.now()) {
  if (!shownPromise) {
    const post = (path, body) => axios.post(apiHost() + '/api/v1' + path, body).then(r => r.data && r.data.data);
    shownPromise = Promise.all([
      getBalansirIndex().then(index => [...new Set([].concat(...index.values()))]),
      post('/balansir/snapshot/list', {}).then(list => Promise.all((list || [])
        .map(s => ({ id: s.id, createdAt: parseTs(s.createdAt) }))
        .filter(s => s.createdAt >= now - SHOWN_DEPTH)
        .map(s => post('/balansir/snapshot/get', { snapshotId: s.id })
          .then(d => ({ createdAt: s.createdAt, data: (d && d.records) || [] }))
          // битый снимок пропускаем, а не роняем всё: иначе нет и текущих правил
          .catch(e => { console.error('Снимок балансировщика не загружен:', s.id, e); return null; }))))
        .then(list => list.filter(Boolean))
        .catch(e => { console.error('Снимки балансировщика не загружены:', e); return []; }),
    ]).then(([records, snapshots]) => ({ records, snapshots }))
      .catch(e => { shownPromise = null; throw e; });
  }
  return shownPromise;
}
